import { Button } from '@/shared/components';
import {
  DEFAULT_BASE_ASSET,
  HIDDEN_BALANCES_FULL_TEXT,
  NUMERIC_CONFIG,
} from '@/shared/config';
import { formatAmount } from '@/shared/lib';
import type { BaseAsset } from '@/shared/types';
import { ChevronDownIcon } from 'lucide-react';
import CloseIcon from '@/shared/assets/close-icon.svg?react';
import DiffPositiveIcon from '@/shared/assets/diff-positive-icon.svg?react';
import DiffNegativeIcon from '@/shared/assets/diff-negative-icon.svg?react';
import CompanySmallIcon from '@/shared/assets/company-small-icon.svg?react';
import { tv } from 'tailwind-variants';

const classes = tv({
  slots: {
    container: 'p-[1.2rem] w-full',
    wrapper: 'flex items-center gap-[1.2rem]',
    avatarWrapper: 'relative shrink-0',
    avatar: 'h-[4.2rem] w-[4.2rem]',
    warningIcon: 'size-[1.8rem] absolute bottom-[-0.3rem] right-[-0.3rem]',
    userInfoWrapper: 'flex flex-col max-w-[25rem]',
    userName:
      'headline4 text-primary truncate min-w-0 gap-[0.8rem] flex flex-row items-center',
    balance: 'headline5 text-primary truncate flex flex-row gap-[0.2rem]',
    deltaPercent: 'headline5 flex flex-row truncate items-center text-primary',
    chevron: 'size-[2.4rem] shrink-0 text-tertiary',
    closeButton: 'w-[4.2rem] h-[4.2rem] border-1 border-border rounded-full',
    closeButtonIcon: 'size-9 w-[2.4rem] h-[2.4rem] text-foreground',
  },
  variants: {
    isPositive: {
      true: {
        deltaPercent: 'text-cyan',
      },
      false: {
        deltaPercent: 'text-red',
      },
    },
  },
});

type Props = {
  isBalancesHidden: boolean;
  userFullName: string;
  isUserVerified?: boolean;
  balance?: number;
  deltaPercent?: number | null;
  baseAsset?: BaseAsset;
  withChevron?: boolean;
  withCloseButton?: boolean;
  isCorporate?: boolean;
  onClick?: () => void;
  onClose?: () => void;
};

export function UserBalance({
  isBalancesHidden,
  userFullName,
  isUserVerified = false,
  balance = 0,
  deltaPercent,
  baseAsset = DEFAULT_BASE_ASSET,
  withChevron = true,
  withCloseButton = false,
  isCorporate = false,
  onClick,
  onClose,
}: Props) {
  const c = classes();

  const balanceNumber = formatAmount(balance, NUMERIC_CONFIG.precision.default);
  const balanceString = `${isBalancesHidden ? HIDDEN_BALANCES_FULL_TEXT : balanceNumber} ${baseAsset}`;

  const hasDeltaPercent = deltaPercent != null;
  const deltaPercentNumber = hasDeltaPercent
    ? formatAmount(Math.abs(deltaPercent), NUMERIC_CONFIG.precision.default)
    : '';
  const isPositive = hasDeltaPercent && deltaPercent >= 0;

  return (
    <div className={c.container()} onClick={onClick}>
      <div className={c.wrapper()}>
        <div className={c.avatarWrapper()}>
          <img
            src="/assets/f2ex-logo-small.svg"
            alt="F2EX"
            className={c.avatar()}
          />

          {!isUserVerified && (
            <img
              src="/assets/warning-status-icon.svg"
              alt="Warning"
              className={c.warningIcon()}
            />
          )}
        </div>

        <div className={c.userInfoWrapper()}>
          <span className={c.userName()} title={userFullName}>
            {isCorporate && (
              <CompanySmallIcon className="w-[1.6rem] h-[1.6rem]" />
            )}
            {userFullName}
          </span>

          <p className={c.balance()} title={balanceString}>
            {balanceString}
            {hasDeltaPercent && (
              <span className={c.deltaPercent({ isPositive })}>
                {isPositive ? <DiffPositiveIcon /> : <DiffNegativeIcon />}{' '}
                {deltaPercentNumber}%
              </span>
            )}
          </p>
        </div>

        {withChevron && <ChevronDownIcon className={c.chevron()} />}

        {withCloseButton && (
          <Button
            variant="icon"
            size="icon"
            onClick={onClose}
            className={c.closeButton()}>
            <CloseIcon className={c.closeButtonIcon()} />
          </Button>
        )}
      </div>
    </div>
  );
}
